import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Group, Paper, Stack, Text } from "@mantine/core";
import { IconInfoCircle } from '@tabler/icons-react';
import PaymentsSummary from './payments-summary';
import SubTradeFees from './sub-trade-fees';
import FacilityPanel from './facilities/facility-panel';
import { RefinedSubTrade, TradedFacilityParams } from 'app/models/traded-facilities-params';
import { MasterTradeParams } from 'app/models/master-trade-params';
import { useListState } from '@mantine/hooks';
import { CustomLoaderPage } from 'app/views/components/loader/loader-page';
import { SignableDocument } from 'app/models/document-params';
import { getSubTradeSignableDocument, loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks';
import { getFMMetadataForSubTrade } from 'app/state/ducks/signable-document/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { FMDocumentMetadata, FMSubTradeFee } from 'app/models/fm-metadata-params';
import { formatDateToShow } from 'app/utils/util-functions';

/**
 * Build TradedFacilityParams from FM endpoint data (single source of truth).
 * contractId (subTradeId) is provided separately since FM identifies the
 * sub-trade at the response root, not per facility.
 */
export const buildFMBackedTradedFacilities = (
  fmMetadata: FMDocumentMetadata,
  subTradeContractId: { id: string; admin: string } | undefined,
): TradedFacilityParams[] => {
  return fmMetadata.tradedFacilityWithCalculatedFee
    .filter(fmTf => fmTf.facilityDetails != null)
    .map(fmTf => {
      // Translate FM's two-field shape (calculatedAmount + nullable overrideAmount)
      // into the legacy SubTradeFacilityFeeData shape the column defs were built for:
      //   - When there IS an override, `otherAmount` carries the calculated default
      //     and `overrideAmount` carries the user's override; both columns render.
      //   - When there is NO override, `otherAmount` is undefined and `overrideAmount`
      //     carries the calculated default (the "single amount to display"); the
      //     Override and Reason columns stay blank.
      const mappedFees = fmTf.fees.map(fee => {
        const hasOverride = fee.overrideAmount != null && fee.overrideAmount !== ''
        return {
          feeType: fee.feeType,
          paidTo: fee.paidTo,
          overrideAmount: hasOverride ? (fee.overrideAmount as string) : fee.calculatedAmount,
          reason: hasOverride ? (fee.reason ?? '') : '',
          otherAmount: hasOverride ? fee.calculatedAmount : undefined,
        }
      })

      return {
        contractId: subTradeContractId,
        facilityId: fmTf.facilityId,
        price: fmTf.price,
        tradeAmount: fmTf.tradeAmount,
        facilityAmount: fmTf.facilityDetails?.amount ?? '0',
        amounts: fmTf.amounts?.map(a => ({
          amount: a.amount,
          effectiveDate: a.effectiveDate,
          transaction: a.transaction ?? '',
          isOverridden: a.isOverridden ?? false,
          // Without the id, the transaction panel cannot match applied actions
          // by transactionId and degrades to positional date matching — which
          // mis-flags applied rows when several same-date entries exist.
          transactionId: a.transactionId ?? null,
        })) ?? [],
        facilityDetails: ({
          id: fmTf.facilityDetails!.id,
          name: fmTf.facilityDetails!.name,
          cusip: fmTf.facilityDetails!.cusip,
          isin: fmTf.facilityDetails!.isin,
          baseCurrency: fmTf.facilityDetails!.baseCurrency,
          // TODO: revisit when currencies are refactored into their own DAR.
          // The FM endpoint only carries baseCurrency, so the Add Fee modal's
          // currency dropdown falls back to the single base currency. Multi-currency
          // facilities will be supported once `currencies` is wired through FMFacility.
          currencies: [fmTf.facilityDetails!.baseCurrency],
          amount: fmTf.facilityDetails!.amount,
          facilityType: fmTf.facilityDetails!.facilityType,
        } as any),
        fees: mappedFees,
      }
    })
}

/**
 * Build MasterTradeParams from FM endpoint data. Only the masterTrade id and
 * agencyAdmin come from the passed contractId (needed for DAML actions),
 * everything else is FM-sourced.
 */
const buildFMBackedMasterTrade = (
  fmMetadata: FMDocumentMetadata,
  masterTradeId: string | undefined,
  agencyAdmin: string | undefined,
): MasterTradeParams => ({
  id: masterTradeId,
  agencyAdmin,
  tradeDate: fmMetadata.tradeDate,
  tradeType: fmMetadata.tradeType,
  tradeAssociation: fmMetadata.tradeAssociation,
  transferType: fmMetadata.transferType,
  interestTreatment: fmMetadata.interestTreatment,
  assignmentFeePayType: fmMetadata.assignmentFeePayType,
  tradeDocsType: fmMetadata.tradeDocsType,
  dealName: fmMetadata.dealName,
  dealId: fmMetadata.dealId,
  delayCompensationDate: fmMetadata.delayCompensationDate,
  paymentType: fmMetadata.paymentType,
  entityName: fmMetadata.sellerName,
  entityMei: fmMetadata.sellerMei,
  counterpartyName: fmMetadata.buyerName,
  counterpartyMei: fmMetadata.buyerMei,
  buyerId: { id: fmMetadata.buyerId, admin: agencyAdmin ?? '' },
  sellerId: { id: fmMetadata.sellerId, admin: agencyAdmin ?? '' },
  includeDelayCompensation:
    fmMetadata.includeDelayCompensation ?? !!fmMetadata.delayCompensationDate,
  disableDCCoC: fmMetadata.disableDCCoC ?? false,
} as unknown as MasterTradeParams)

interface DetailPanelProps {
  refinedSubTrade: RefinedSubTrade;
  isExpanded: boolean;
}


const SubTradeEntryPanelAllocations: React.FC<DetailPanelProps> = React.memo(({ refinedSubTrade }) => {
  const dispatch = useDispatch()
  const [expended, expendedHandler] = useListState<boolean>([false].concat(refinedSubTrade.tradedFacilities.map(() => false)));
  const [signableDocumentResponse, setSignableDocumentResponse] = useState<SignableDocument | null>(null);
  const [aaSignableDoc, setAaSignableDoc] = useState<SignableDocument | null>(null);

  const subTradeId = refinedSubTrade?.id ?? ''
  const { data: fmMetadata, error: fmError } = useSelector(
    (state: any) => getFMMetadataForSubTrade(state, subTradeId),
  )

  const isTerminated = refinedSubTrade.status === 'Terminated'
  // When all underlying facilities are terminated, fee calculations on the
  // ledger (cost of carry, delayed compensation) hit a pre-existing divide
  // by zero because the facility's amount history ends with 0. Treat the
  // panel the same as a terminated sub-trade in that case — skip the FM
  // load and render a static notice so the user can proceed to Terminate.
  const allFacilitiesTerminated =
    refinedSubTrade.tradedFacilities.length > 0 &&
    refinedSubTrade.tradedFacilities.every(
      (tf) => tf.facilityDetails?.status === 'Terminated',
    )
  const fmDataUnavailable = isTerminated || allFacilitiesTerminated

  // Refetch FM metadata on every expand. The detail panel is unmounted when the
  // row collapses (sub-trade-entry-panel.tsx renderDetailPanel returns null), so
  // this effect runs each time the user reopens a sub-trade — picking up any
  // changes made elsewhere (other sub-trades, facility-loans tab, etc.) without
  // needing call sites to invalidate the cache on every mutation. Skip the
  // dispatch entirely when FM data is unavailable (terminated sub-trade, or
  // all underlying facilities terminated) — the endpoint can't assemble a
  // response and the panel renders a static notice instead.
  useEffect(() => {
    if (!subTradeId || fmDataUnavailable) return
    dispatch(loadFMMetadataForSubTrade(subTradeId) as any)
  }, [subTradeId, fmDataUnavailable, dispatch])


  useEffect(() => {
    if (!refinedSubTrade?.id || fmDataUnavailable) return;
    getSubTradeSignableDocument(refinedSubTrade.id, "FundingMemoDocument").then(response => {
      if (response.success) {
        setSignableDocumentResponse(response.payload);
      } else {
        setSignableDocumentResponse(null);
      }
    });
  }, [refinedSubTrade?.id, fmDataUnavailable]);

  useEffect(() => {
    if (!refinedSubTrade?.id || fmDataUnavailable) return;
    getSubTradeSignableDocument(refinedSubTrade.id, "AssignmentAgreementDocument").then(response => {
      if (response.success) {
        setAaSignableDoc(response.payload);
      } else {
        setAaSignableDoc(null);
      }
    });
  }, [refinedSubTrade?.id, fmDataUnavailable]);

  // Build FM-backed traded facilities (single source of truth when FM is loaded)
  const tradedFacilityWithCalculatedFee = useMemo<TradedFacilityParams[]>(() => {
    if (!fmMetadata) return []
    const subTradeContractId = refinedSubTrade.id
      ? { id: refinedSubTrade.id, admin: refinedSubTrade.agencyAdmin ?? '' }
      : undefined
    return buildFMBackedTradedFacilities(fmMetadata, subTradeContractId)
  }, [fmMetadata, refinedSubTrade.id, refinedSubTrade.agencyAdmin])

  // Build FM-backed master trade
  const fmBackedMasterTrade = useMemo<MasterTradeParams | undefined>(() => {
    if (!fmMetadata) return undefined
    return buildFMBackedMasterTrade(
      fmMetadata,
      refinedSubTrade.masterTradeId?.id,
      refinedSubTrade.masterTradeId?.admin,
    )
  }, [fmMetadata, refinedSubTrade.masterTradeId])

  // Build FM-backed subTrade view used for display in child components.
  // Identity-only fields (id, masterTradeId, status, agencyAdmin, systemId, clientId, entities)
  // flow through from the parent prop because FM endpoint does not carry them.
  const fmBackedSubTrade = useMemo<RefinedSubTrade>(() => {
    if (!fmMetadata || !fmBackedMasterTrade) return refinedSubTrade
    return {
      ...refinedSubTrade,
      targetSettlementDate: fmMetadata.targetSettlementDate ?? refinedSubTrade.targetSettlementDate,
      delayCompensationDate: fmMetadata.delayCompensationDate ?? refinedSubTrade.delayCompensationDate,
      masterTrade: fmBackedMasterTrade,
      tradedFacilities: tradedFacilityWithCalculatedFee,
      fees: fmMetadata.subTradeFees?.map((f: FMSubTradeFee) => ({
        feeType: f.feeType,
        paidTo: f.paidTo,
        feeAmount: f.feeAmount,
        currency: f.currency,
        notes: f.notes ?? '',
      })) ?? refinedSubTrade.fees,
    }
  }, [refinedSubTrade, fmMetadata, fmBackedMasterTrade, tradedFacilityWithCalculatedFee])

  // Use signable document from FM metadata when available
  const fmSignableDoc = fmMetadata?.signableDocument ?? signableDocumentResponse

  // Show the loader only when we have nothing to display. Background refetches
  // (every re-expand) keep the previous data visible and swap in the new data
  // when the request resolves — no loader flash on re-expand.
  const isAwaitingFm = !fmMetadata && !fmError

  const terminationReason =
    isTerminated &&
      typeof refinedSubTrade.terminationComment === 'string' &&
      refinedSubTrade.terminationComment.length > 0
      ? refinedSubTrade.terminationComment
      : null

  return (
    <Stack className="panel-container" w="100%">
      {terminationReason && (
        <Paper className="panel-overview" shadow="sm">
          <Group
            position='left'
            style={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              alignSelf: 'stretch',
              backgroundColor: '#FFF',
            }}
          >
            <Text className='cancellation-reason-label' style={{ textAlign: 'left' }}>
              Reason for Termination:
            </Text>
            <Text className='cancellation-reason-value' style={{ textAlign: 'left' }}>
              {terminationReason}
            </Text>
          </Group>
        </Paper>
      )}
      {!isTerminated && (
        <Paper className="panel-overview" shadow="sm" >
          {!subTradeId ? (
            <Alert color="red" title="Missing sub-trade id" style={{ margin: '10px' }}>
              <Text size="sm">Cannot load funding-memo data without a sub-trade id.</Text>
            </Alert>
          ) : allFacilitiesTerminated ? (
            <Alert
              icon={<IconInfoCircle size={18} style={{ color: '#476DB9' }} />}
              color="blue"
              variant="light"
              title={
                refinedSubTrade.tradedFacilities.length > 1
                  ? 'Underlying facilities terminated'
                  : 'Underlying facility terminated'
              }
              style={{ margin: '10px', alignSelf: 'stretch' }}
              styles={{
                root: { backgroundColor: '#FFF' },
                title: { fontSize: '16px' },
                icon: {
                  width: '20px',
                  height: '20px',
                  minWidth: '18px',
                  color: '#476DB9',
                },
              }}
            >
              <Text style={{ fontSize: '14px', textAlign: 'left' }}>
                {refinedSubTrade.tradedFacilities.length > 1
                  ? 'Details are not available because the underlying facilities have been terminated. Use the Terminate button on the Documents tab to terminate this sub-trade.'
                  : 'Details are not available because the underlying facility has been terminated. Use the Terminate button on the Documents tab to terminate this sub-trade.'}
              </Text>
            </Alert>
          ) : fmError ? (
            <Alert color="red" title="Error loading data" style={{ margin: '10px' }}>
              <Text size="sm">{fmError}</Text>
            </Alert>
          ) : isAwaitingFm ? (
            <CustomLoaderPage />
          ) : (
            <>
            {fmMetadata?.lockedStatus && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                color="blue"
                variant="light"
                style={{ margin: '10px 10px 0 10px' }}
              >
                FM Locked — showing data as of {formatDateToShow(new Date(fmMetadata.createdAt))}
              </Alert>
            )}
            <PaymentsSummary refinedSubTrade={fmBackedSubTrade} tradedFacilityWithCalculatedFee={tradedFacilityWithCalculatedFee} fmMetadata={fmMetadata ?? undefined} />
            <SubTradeFees refinedSubTrade={fmBackedSubTrade} fmSignableDoc={fmSignableDoc} fmMetadata={fmMetadata ?? undefined} expended={expended[0]} setExpended={(item) => expendedHandler.setItem(0, item)} />
            {tradedFacilityWithCalculatedFee
              .slice()
              .sort((a, b) => {
                const nameA = a.facilityDetails?.name?.toLowerCase() || ''
                const nameB = b.facilityDetails?.name?.toLowerCase() || ''
                return nameA.localeCompare(nameB)
              })
              .map((facility, index) => {
                const refinedSubTradeFacilityFees = facility.fees ?? []
                return (
                  <FacilityPanel key={facility.facilityId.id} facility={facility} fmSignableDoc={fmSignableDoc} aaSignableDoc={aaSignableDoc} masterTrade={fmBackedMasterTrade ?? refinedSubTrade.masterTrade} subTrade={fmBackedSubTrade} refinedSubTradeFacilityFees={refinedSubTradeFacilityFees} fmFacilityAmounts={fmMetadata?.facilityAmounts} fmFeeSections={fmMetadata?.feeSections} fmLocked={fmMetadata?.lockedStatus} expended={expended[index + 1]} setExpended={(item) => expendedHandler.setItem(index + 1, item)} />
                )
              })}
            </>
          )}
        </Paper>
      )}
    </Stack >
  );
});

export default SubTradeEntryPanelAllocations;